import { canManageTenantRole, PERMISSION_FLAGS, } from './permissions.js';
import { MILLISECONDS_PER_HOUR } from './date.js';
/**
 * Anteprima permessi: OWNER/ADMIN vedono la webapp con ruolo e flag di un
 * altro membro del tenant, in sola lettura. Lo stato vive sulla sessione
 * corrente (`Session.permissionPreview`), non su un token separato.
 */
/** Durata fissa dell'anteprima: non si rinnova con l'uso. */
export const PERMISSION_PREVIEW_TTL_MS = 2 * MILLISECONDS_PER_HOUR;
/** Motivi per cui un'anteprima si chiude, riportati nell'audit e al client. */
export const PERMISSION_PREVIEW_END_REASONS = {
    MANUAL: 'MANUAL',
    EXPIRED: 'EXPIRED',
    TARGET_UNAVAILABLE: 'TARGET_UNAVAILABLE',
    TARGET_OUT_OF_RANK: 'TARGET_OUT_OF_RANK',
    ACTOR_NOT_ALLOWED: 'ACTOR_NOT_ALLOWED',
    INVALID: 'INVALID',
    LOGOUT: 'LOGOUT',
    /** Sessione revocata d'ufficio (revoca massiva, ruolo revocato, pulizia). */
    SESSION_REVOKED: 'SESSION_REVOKED',
    /**
     * Solo verso il client, mai nell'audit: la tab crede di essere in
     * un'anteprima (header `X-Permission-Preview`) che sulla sessione non c'è
     * più, chiusa altrove (altra tab, altro dispositivo, pagina ripresa dalla
     * cache del browser) o sostituita da un'altra.
     */
    ALREADY_ENDED: 'ALREADY_ENDED',
};
/**
 * Header con cui la webapp dichiara l'anteprima in cui crede di essere
 * (`startedAt` dell'anteprima letta da `/auth/me`). Il backend rifiuta con
 * 409 `ended` (`ALREADY_ENDED`) le richieste di una tab rimasta indietro,
 * così la sua UI di sola lettura non sopravvive all'anteprima vera.
 */
export const PERMISSION_PREVIEW_CLIENT_HEADER = 'X-Permission-Preview';
/**
 * Permessi effettivi dell'anteprima: un flag è attivo solo se lo hanno sia il
 * target sia l'attore, così l'anteprima non concede mai più di quanto
 * l'attore già veda. Un attore OWNER (`ALL_PERMISSION_FLAGS_GRANTED`) riceve
 * i flag del target.
 */
export function intersectPermissionFlags(target, actor) {
    return Object.fromEntries(PERMISSION_FLAGS.map((flag) => [
        flag,
        target[flag] === true && actor[flag] === true,
    ]));
}
/** Flag attivi nel target ma assenti nell'attore, nell'ordine canonico. */
export function listRestrictedFlags(target, actor) {
    return PERMISSION_FLAGS.filter((flag) => target[flag] === true && actor[flag] !== true);
}
/**
 * Regole di ruolo dell'anteprima, identiche su backend (avvio e ogni
 * richiesta) e webapp (voce di menu): mai su sé stessi, mai su un OWNER,
 * attore OWNER oppure con `canManageUsers`, e di grado superiore al target.
 * Restituisce il motivo del rifiuto, `null` se le regole sono rispettate.
 */
export function findPermissionPreviewRoleIneligibility({ actorRole, actorCanManageUsers, targetRole, isSelf, }) {
    if (isSelf)
        return PERMISSION_PREVIEW_END_REASONS.TARGET_UNAVAILABLE;
    if (targetRole === 'OWNER') {
        return PERMISSION_PREVIEW_END_REASONS.TARGET_OUT_OF_RANK;
    }
    if (actorRole !== 'OWNER' && !actorCanManageUsers) {
        return PERMISSION_PREVIEW_END_REASONS.ACTOR_NOT_ALLOWED;
    }
    if (!canManageTenantRole(actorRole, targetRole, isSelf)) {
        return PERMISSION_PREVIEW_END_REASONS.TARGET_OUT_OF_RANK;
    }
    return null;
}
/** `true` se le regole di ruolo consentono di avviare l'anteprima. */
export function canStartPermissionPreview(input) {
    return findPermissionPreviewRoleIneligibility(input) === null;
}
//# sourceMappingURL=permission-preview.js.map